import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import styled from "styled-components";
import { DateOfBirthSelector } from "../../../components/DateOfBirth/DateOfBirthSelector";
import {
  TextArea,
  MobileInput,
  Description,
  Dropdown,
  CheckboxWithLabel,
  Button,
  BookingSummary,
} from "../../../components/index";
import { Input } from "../../../components/Input/Input";
import { ContactDetails, State } from "../../../types";
import {
  BookingSummaryStyles,
  ButtonStyles,
  DescriptionStyles,
  DropdownStyles,
} from "../../../types/styles";
import { useTranslation } from "react-i18next";
import DateHelper from "../../../helpers/DateHelper";
import SelectedVenueDetailsHelper from "../../../helpers/SelectedVenueDetailsHelper";
import { defaultThemeStyles } from "../../../mocks";
import ReturnTableNotice from "../../../components/ReturnTableNotice/ReturnTableNotice";
import {
  AppearanceMode,
  Turnstile,
  WidgetSize,
} from "@marsidev/react-turnstile";
import { useState } from "react";

const StyledInput = styled(Input)``;

const StyledFormContainer = styled.form`
  display: grid;
  row-gap: 8px;
  font-size: 13px;

  p {
    margin: 0;
  }
`;

const StyledCheckboxContainer = styled.div`
  column-gap: 15px;
  justify-content: left;
  grid-template-columns: auto auto;
  display: grid;
  margin-bottom: 10px;
`;

const StyledFieldAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StyledCheckboxAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 5px 0 10px 0;
  font-weight: 700;
`;

const StyledDescription = styled(Description)`
  font-weight: 700;
  font-size: 13px;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TurnstileContainer = styled.div<{ showTurnstile: boolean }>`
  height: ${(props) => (props.showTurnstile ? "auto" : "0px")};
`;

const TurnstileErrorSpan = styled.span`
  color: red;
`;

export type OptionalFormFieldsTypes = {
  showTitle?: boolean;
  showTelephoneNumber?: boolean;
  showCompany?: boolean;
  showPostcode?: boolean;
  postcodeRequired?: boolean;
  showHouseholds?: boolean;
  showComments?: boolean;
  showDateOfBirth?: boolean;
  showDateOfBirthYear?: boolean;
  dateOfBirthRequired?: boolean;
  dateOfBirthDescription?: string;
  showSmsMarketing?: boolean;
  showGroupMarketing?: boolean;
  showCustomField?: boolean;
  showMembershipId?: boolean;
  membershipIdRequired?: boolean;
  membershipIdTitle?: string;
  precheckVenueEmailOptin?: boolean;
  showTurnstile?: boolean;
};

export type DetailsFormProps = {
  state: State;
  formData?: ContactDetails;
  setFormData: (data: ContactDetails) => void;
  optionalFormFields?: OptionalFormFieldsTypes;
  styles: DescriptionStyles &
    ButtonStyles &
    DropdownStyles &
    BookingSummaryStyles;
};

export const DetailsForm: React.FC<DetailsFormProps> = ({
  state,
  optionalFormFields,
  formData,
  setFormData,
  styles,
}) => {
  const { t } = useTranslation();
  const { bookingDetails } = state;
  const isIndividualVenueMarketingEnabled =
    state.crossSellSetup?.IsIndividualVenueMarketingEnabled;
  const venueName = state.isCrossSellChosen
    ? state.crossSellSetup?.CrossSellVenues.filter(
        (csv) => csv.MicrositeName === bookingDetails?.timeSlot?.MicrositeName
      )[0].Name
    : state.crossSellSetup?.Name || "";

  const groupName = state.crossSellSetup?.GroupMarketingDisplayName || "";

  const [showTurnstile, setShowTurnstile] = useState(false);

  // Build the schema dynamically based on optionalFormFields
  const buildSchema = (optionalFields?: OptionalFormFieldsTypes) => {
    const baseSchema = Yup.object().shape({
      FirstName: Yup.string().required(t("RequireFirstName")),
      Surname: Yup.string().required(t("RequireLastName")),
      MobileCountryCode: Yup.number().required().default(44),
      Mobile: Yup.string()
        .required(t("RequireMobileNumber"))
        .min(5, t("RequireMobileNumber")),
      MobileAlpha2Code: Yup.string().default("GB"),
      Email: Yup.string().email().required(t("RequireEmail")),
      ReceiveRestaurantEmailMarketing: Yup.boolean(),
    });

    const additionalVisibleFields = Yup.object().shape({
      ...(optionalFields?.showTitle && {
        Title: Yup.string(),
      }),
      ...(optionalFields?.showTelephoneNumber && {
        PhoneCountryCode: Yup.number().default(44),
        Phone: Yup.string(),
        PhoneAlpha2Code: Yup.string().default("GB"),
      }),
      ...(optionalFields?.showCompany && {
        Company: Yup.string(),
      }),
      ...(optionalFields?.showHouseholds && {
        Households: Yup.number(),
      }),
      ...(optionalFields?.showComments && {
        SpecialRequests: Yup.string(),
      }),
      ...(optionalFields?.showSmsMarketing && {
        ReceiveRestaurantSmsMarketing: Yup.boolean(),
      }),
      ...(optionalFields?.showGroupMarketing && {
        ReceiveGroupEmailMarketing: Yup.boolean(),
      }),
      ...(optionalFields?.showGroupMarketing &&
        optionalFields.showSmsMarketing && {
          ReceiveGroupSmsMarketing: Yup.boolean(),
        }),
      ...(optionalFields?.showCustomField && {
        CustomField: state?.customField?.IsRequired
          ? Yup.string().required(
              t("CustomFieldRequired", {
                fieldName: state.customField.Name,
              })
            )
          : Yup.string().nullable(),
      }),
      ...(optionalFields?.showTurnstile && {
        TurnstileToken: state.crossSellSetup?.EnforceTurnstile
          ? Yup.string()
              .required(t("TurnstileIncompleteError"))
              .test(
                "is-not-error",
                t("TurnstileFailureError"),
                (value) => value !== "error"
              )
          : Yup.string().required(t("TurnstileIncompleteError")),
      }),
    });

    const additionalVisibleOptionalRequiredFields = Yup.object().shape({
      ...(optionalFields?.showDateOfBirth && {
        Birthday: Yup.date()
          .nullable()
          .when([], {
            is: () => optionalFields.dateOfBirthRequired,
            then: (schema) => schema.required(t("RequireDOB")),
            otherwise: (schema) => schema,
          }),
      }),
      ...(optionalFields?.showPostcode && {
        Postcode: Yup.string().when([], {
          is: () => optionalFields.postcodeRequired,
          then: (schema) => schema.required(t("RequirePostcode")),
          otherwise: (schema) => schema,
        }),
      }),
      ...(optionalFields?.showMembershipId && {
        MembershipId: Yup.string().when([], {
          is: () => optionalFields.membershipIdRequired,
          then: (schema) => schema.required(t("RequireMembershipId")),
          otherwise: (schema) => schema,
        }),
      }),
    });

    return baseSchema
      .concat(additionalVisibleFields)
      .concat(additionalVisibleOptionalRequiredFields);
  };

  const schema: Yup.ObjectSchema<ContactDetails> =
    buildSchema(optionalFormFields);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<ContactDetails>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...formData,
      ReceiveRestaurantEmailMarketing:
        state.crossSellSetup?.ThemeSettings?.PrecheckVenueEmailOptin || false,
    },
  });

  const onSubmit = (data: ContactDetails) => {
    const updatedData = data;
    if (!updatedData.Phone || updatedData.Phone === "NaN") {
      delete updatedData.PhoneAlpha2Code;
      delete updatedData.PhoneCountryCode;
      delete updatedData.Phone;
    }
    setFormData(updatedData);
  };

  return (
    <StyledFormContainer noValidate onSubmit={handleSubmit(onSubmit)}>
      {optionalFormFields?.showTitle && (
        <Controller
          name="Title"
          control={control}
          render={({ field }) => (
            <StyledInput
              label={t("TitleLabel")}
              value={field.value as string}
              onChange={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.Title?.message}
              dataId="title"
            />
          )}
        />
      )}
      <Controller
        name="FirstName"
        control={control}
        render={({ field }) => (
          <StyledInput
            label={t("FirstName")}
            value={field.value as string}
            onChange={(value) => field.onChange(value)}
            styles={styles}
            errorMessage={errors.FirstName?.message}
            dataId="first-name"
          />
        )}
      />
      <Controller
        name="Surname"
        control={control}
        render={({ field }) => (
          <StyledInput
            label={t("LastName")}
            value={field.value as string}
            onChange={(value) => field.onChange(value)}
            styles={styles}
            errorMessage={errors.Surname?.message}
            dataId="last-name"
          />
        )}
      />
      {optionalFormFields?.showCompany && (
        <Controller
          name="Company"
          control={control}
          render={({ field }) => (
            <StyledInput
              label={t("Company")}
              value={field.value as string}
              onChange={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.Company?.message}
              dataId="company"
            />
          )}
        />
      )}
      {optionalFormFields?.showTelephoneNumber && (
        <Controller
          name="Phone"
          control={control}
          render={({ field }) => (
            <MobileInput
              displayLabel={t("TelephoneNumber")}
              selectedPhoneNumber={field.value as string}
              selectedAlpha2Code={formData?.PhoneAlpha2Code as string}
              setCountryCode={(value) => {
                setValue("PhoneCountryCode", value as number);
              }}
              setCountryAlphaCode={(value) => {
                setValue("PhoneAlpha2Code", value as string);
              }}
              setMobileNumber={(number) => field.onChange(number)}
              styles={styles}
              dataId="telephone-number"
            />
          )}
        />
      )}
      <Controller
        name="Mobile"
        control={control}
        render={({ field }) => (
          <MobileInput
            displayLabel={t("MobileNumber")}
            selectedPhoneNumber={field.value}
            selectedAlpha2Code={formData?.MobileAlpha2Code as string}
            setCountryCode={(value) => {
              setValue("MobileCountryCode", value as number);
            }}
            setCountryAlphaCode={(value) => {
              setValue("MobileAlpha2Code", value as string);
            }}
            setMobileNumber={(number) => field.onChange(number)}
            styles={styles}
            errorMessage={errors.Mobile?.message}
            dataId="mobile-number"
          />
        )}
      />
      <Controller
        name="Email"
        control={control}
        render={({ field }) => (
          <StyledInput
            type="email"
            label={t("Email")}
            value={field.value as string}
            onChange={(value) => field.onChange(value)}
            styles={styles}
            errorMessage={errors.Email?.message}
            dataId="email"
          />
        )}
      />
      {optionalFormFields?.showMembershipId && (
        <Controller
          name={"MembershipId"}
          control={control}
          render={({ field }) => (
            <StyledInput
              type="text"
              label={optionalFormFields?.membershipIdTitle || t("MembershipId")}
              value={field.value as string}
              onChange={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.MembershipId?.message}
              dataId="membership-id"
            />
          )}
        />
      )}
      {optionalFormFields?.showPostcode && (
        <Controller
          name="Postcode"
          control={control}
          render={({ field }) => (
            <StyledInput
              label={t("Postcode")}
              value={field.value as string}
              onChange={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.Postcode?.message}
              dataId="postcode"
            />
          )}
        />
      )}
      {optionalFormFields?.showDateOfBirth && (
        <StyledFieldAndDescriptionContainer>
          <Controller
            name="Birthday"
            control={control}
            render={({ field }) => (
              <DateOfBirthSelector
                styles={styles}
                dateOfBirth={field.value as Date | null}
                setDateOfBirth={(date) => field.onChange(date)}
                showDateOfBirthYear={optionalFormFields.showDateOfBirthYear}
                errorMessage={errors.Birthday?.message}
              />
            )}
          />
          {optionalFormFields?.dateOfBirthDescription && (
            <Description styles={styles}>
              {optionalFormFields.dateOfBirthDescription}
            </Description>
          )}
        </StyledFieldAndDescriptionContainer>
      )}
      {optionalFormFields?.showCustomField && (
        <Controller
          name="CustomField"
          control={control}
          render={({ field }) => (
            <StyledInput
              label={state.customField!.Name}
              value={field.value as string}
              onChange={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.CustomField?.message}
              dataId="custom-field"
            />
          )}
        />
      )}
      {optionalFormFields?.showHouseholds && (
        <Controller
          name="Households"
          control={control}
          render={({ field }) => (
            <Dropdown
              options={[
                { label: "1", value: 1 },
                { label: "2", value: 2 },
                { label: "3", value: 3 },
                { label: "4", value: 4 },
                { label: "5", value: 5 },
              ]}
              label={t("Households")}
              selected={field.value as number}
              onSelect={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.Households?.message}
              dataId="households-dropdown"
            />
          )}
        />
      )}
      {optionalFormFields?.showComments && (
        <Controller
          name="SpecialRequests"
          control={control}
          render={({ field }) => (
            <TextArea
              label={t("Comments")}
              value={field.value as string}
              onChange={(value) => field.onChange(value)}
              styles={styles}
              errorMessage={errors.SpecialRequests?.message}
              maxLength={2000}
              showCounter
              dataId={"comments"}
            />
          )}
        />
      )}
      {isIndividualVenueMarketingEnabled && (
        <StyledCheckboxAndDescriptionContainer>
          <StyledDescription styles={styles}>
            {t("WouldYouLikeToReceiveNewsAndOffers", { venueName })}
          </StyledDescription>
          <StyledCheckboxContainer>
            <Controller
              name="ReceiveRestaurantEmailMarketing"
              control={control}
              render={({ field }) => (
                <CheckboxWithLabel
                  children={<label>{t("Email")}</label>}
                  isChecked={field.value as boolean}
                  onChecked={(value) => field.onChange(value)}
                  styles={styles}
                  boldStyle
                  dataId="restaurant-email-marketing"
                  forceChecked={
                    state.crossSellSetup?.ThemeSettings?.PrecheckVenueEmailOptin
                  }
                />
              )}
            />
            {optionalFormFields?.showSmsMarketing && (
              <Controller
                name="ReceiveRestaurantSmsMarketing"
                control={control}
                render={({ field }) => (
                  <CheckboxWithLabel
                    children={<label>{t("Sms")}</label>}
                    isChecked={field.value as boolean}
                    onChecked={(value) => field.onChange(value)}
                    styles={styles}
                    boldStyle
                    dataId="restaurant-sms-marketing"
                  />
                )}
              />
            )}
          </StyledCheckboxContainer>
        </StyledCheckboxAndDescriptionContainer>
      )}
      {optionalFormFields?.showGroupMarketing && (
        <StyledCheckboxAndDescriptionContainer>
          <StyledDescription styles={styles}>
            {t("WouldYouLikeToReceiveNewsAndOffers", { venueName: groupName })}
          </StyledDescription>
          <StyledCheckboxContainer>
            <Controller
              name="ReceiveGroupEmailMarketing"
              control={control}
              render={({ field }) => (
                <CheckboxWithLabel
                  children={<label>{t("Email")}</label>}
                  isChecked={field.value as boolean}
                  onChecked={(value) => field.onChange(value)}
                  styles={styles}
                  boldStyle
                  dataId="group-email-marketing"
                />
              )}
            />
            {optionalFormFields?.showSmsMarketing && (
              <Controller
                name="ReceiveGroupSmsMarketing"
                control={control}
                render={({ field }) => (
                  <CheckboxWithLabel
                    children={<label>{t("Sms")}</label>}
                    isChecked={field.value as boolean}
                    onChecked={(value) => field.onChange(value)}
                    styles={styles}
                    boldStyle
                    dataId="group-sms-marketing"
                  />
                )}
              />
            )}
          </StyledCheckboxContainer>
        </StyledCheckboxAndDescriptionContainer>
      )}
      {optionalFormFields?.showTurnstile && (
        <Controller
          name="TurnstileToken"
          control={control}
          render={({ field }) => {
            const appearance =
              (state.crossSellSetup?.TurnstileAppearance as AppearanceMode) ||
              "interaction-only";
            const shouldShowTurnstile =
              appearance === "always" ||
              showTurnstile ||
              (errors.TurnstileToken !== null &&
                errors.TurnstileToken !== undefined &&
                errors.TurnstileToken.message !== "");
            return (
              <TurnstileContainer showTurnstile={shouldShowTurnstile}>
                <Turnstile
                  siteKey={state.crossSellSetup?.TurnstileSiteKey || ""}
                  onSuccess={(token) => field.onChange(token)}
                  onError={() => field.onChange("error")}
                  options={{
                    size:
                      (state.crossSellSetup?.TurnstileSize as WidgetSize) ||
                      "normal",
                    appearance,
                  }}
                  onBeforeInteractive={() => {
                    setShowTurnstile(true);
                  }}
                  style={{ width: "100%", display: "block" }}
                />
                {errors.TurnstileToken && (
                  <TurnstileErrorSpan>
                    {errors.TurnstileToken?.message}
                  </TurnstileErrorSpan>
                )}
              </TurnstileContainer>
            );
          }}
        />
      )}

      <FooterContainer>
        <BookingSummary
          flashBookingSummaryPanel={state.flashBookingSummaryPanel || false}
          date={bookingDetails.selectedDate}
          numberOfCovers={bookingDetails.partySize}
          timeslot={
            bookingDetails.timeSlot
              ? DateHelper.sliceTimeSlot(bookingDetails.timeSlot)
              : null
          }
          styles={styles}
          {...(state.isCrossSellChosen && {
            newProvider: {
              name:
                SelectedVenueDetailsHelper.getVenueDetails(state).name || "",
              location:
                SelectedVenueDetailsHelper.getVenueDetails(state).location ||
                "",
              locationUrl:
                SelectedVenueDetailsHelper.getVenueDetails(state).url || "",
            },
          })}
        />
        {state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired &&
          state.bookingDetails.timeSlot?.TimeSlot.LeaveTime && (
            <ReturnTableNotice
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              leaveTime={
                state.promotionDetails?.leaveTime.length
                  ? state.promotionDetails.leaveTime
                  : state.bookingDetails.timeSlot?.TimeSlot.LeaveTime
              }
              isPromotion={!!state.promotionDetails?.leaveTime.length}
            />
          )}
        <Button
          styles={styles}
          type="submit"
          rightArrowIcon
          dataId="next-button"
        >
          {t("Next")}
        </Button>
      </FooterContainer>
    </StyledFormContainer>
  );
};

export default DetailsForm;
